import { useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'next-i18next';
import Aside from '@components/Aside/Aside';
import CategoriesFilterSelect from '@features/categories/components/CategoriesFilterSelect/CategoriesFilterSelect';
import CategoriesFilterMultiselect from '@features/categories/components/CategoriesFilterMultiselect/CategoriesFilterMultiselect';
import CategoriesFilterBoolean from '@features/categories/components/CategoriesFilterBoolean/CategoriesFilterBoolean';
import CategoriesFilterRange from '@features/categories/components/CategoriesFilterRange/CategoriesFilterRange';
import CategoriesFilterPanel from '@features/categories/components/CategoriesFilterPanel/CategoriesFilterPanel';
import styles from '@features/categories/components/CategoriesFilters/CategoriesFilters.styles';
import FilterSelectState from '@features/categories/mobx/state/FilterSelectState';
import FilterMultiselectState from '@features/categories/mobx/state/FilterMultiselectState';
import FilterRangeState from '@features/categories/mobx/state/FilterRangeState';
import FilterState from '@features/categories/mobx/state/FilterState';
import ErrorBoundary from '@components/errors/ErrorBoundary';
import Heading from '@components/Heading/Heading';
import Icon from '@components/icons/Icon';
import Button from '@components/Button/Button';
import Add from '@components/icons/components/Add';
import ArrowBack from '@components/icons/components/ArrowBack';
import FilterBooleanState from '@features/categories/mobx/state/FilterBooleanState';
import layoutUiState from '@features/layout/mobX/LayoutUiState';
const CategoriesFilters = ({
  filtersStore
}) => {
  const {
    filters,
    sorting,
    resetFilters,
    filterKeys: filtersIterator,
    asideActive,
    toggleAside,
    pagination,
    setActiveFilterView,
    setAllFiltersViewActive,
    allFiltersViewActive,
    activeFilterView,
    virtualCategorySlug
  } = filtersStore;
  const {
    t
  } = useTranslation('filters');
  const {
    setIsCategoriesFiltersAsideActive
  } = layoutUiState;
  const setFilterView = useCallback((filterName: string, showAll: boolean) => {
    setActiveFilterView(filterName);
    setAllFiltersViewActive(showAll);
  }, [setActiveFilterView, setAllFiltersViewActive]);
  const resetFilterView = useCallback(() => setFilterView('filters', false), [setFilterView]);
  useEffect(() => {
    if (!asideActive) resetFilterView();
    setIsCategoriesFiltersAsideActive(asideActive);
  }, [asideActive, resetFilterView, virtualCategorySlug]);
  const renderFilterLink = (filter: FilterState) => <li key={filter.key} data-cy="FiltersLink">
      <Button variant="link" size="small" fullWidth letterCase="uppercase" onClick={() => setFilterView(filter.name, true)} addSx={styles.filterLinkButton}>
        <span>{t(filter.name.toLowerCase()) || filter.name}</span>
        <Icon size="18px">
          <Add />
        </Icon>
      </Button>
    </li>;
  const renderSortingLink = () => sorting && <li key={sorting.key} data-cy="SortingLink">
        <Button variant="link" size="small" fullWidth letterCase="uppercase" onClick={() => setFilterView('sort', true)} addSx={styles.filterLinkButton}>
          <span>{t(sorting.name.toLowerCase()) || sorting.name}</span>
          <Icon size="18px">
            <Add />
          </Icon>
        </Button>
      </li>;
  const renderFilterComponent = (filter: FilterState) => <div key={filter.key} sx={{
    display: activeFilterView === filter.name ? 'block' : 'none'
  }}>
      {filter instanceof FilterSelectState && <CategoriesFilterSelect key={filter.key} filter={filter} />}
      {filter instanceof FilterMultiselectState && <CategoriesFilterMultiselect key={filter.key} filter={filter} />}
      {filter instanceof FilterBooleanState && <CategoriesFilterBoolean key={filter.key} filter={filter} />}
      {filter instanceof FilterRangeState && <CategoriesFilterRange key={filter.key} filter={filter} forceRefresh={activeFilterView === filter.name} />}
    </div>;
  const renderSortingComponent = () => sorting && <div key={sorting.key} sx={{
    display: activeFilterView === 'sort' ? 'block' : 'none'
  }}>
        <CategoriesFilterSelect key={sorting.key} filter={sorting} sort />
      </div>;
  const handleAsideClose = useCallback(() => {
    toggleAside();
  }, [toggleAside]);
  return <ErrorBoundary>
      <Aside data-cy="CategoriesFilters" active={asideActive} onCloseAside={handleAsideClose}>
        <div data-cy="CategoriesFiltersContainer" sx={styles.filterContainer}>
          <div sx={styles.filterHeaderContainer} data-cy="CategoriesFiltersContainerTitle">
            {allFiltersViewActive && <Button addSx={styles.backButton} variant="iconOnly" onClick={resetFilterView} cy="CategoriesFiltersContainerBackButton">
                <Icon size={['16px', '16px', '16px', '16px', '16px', '21px']}>
                  <ArrowBack />
                </Icon>
              </Button>}
            <div sx={styles.headerContainer}>
              <Heading h={3} headingType={4} bold align="center">
                {t(activeFilterView.toLowerCase()) || activeFilterView}
              </Heading>
            </div>
          </div>
          <div sx={styles.filterContent(!allFiltersViewActive)}>
            <ul data-cy="CategoriesFilters__links" sx={styles.filterLinkList(!allFiltersViewActive)}>
              {filtersIterator.map(filterName => {
              const filter = filters.get(filterName);
              if (filter) return renderFilterLink(filter);
              return null;
            })}
              {renderSortingLink()}
            </ul>
          </div>

          <div sx={styles.filterContent(allFiltersViewActive)}>
            {filtersIterator.map(filterName => {
            const filter = filters.get(filterName);
            if (filter) return renderFilterComponent(filter);
            return null;
          })}
            {renderSortingComponent()}
          </div>

          <div sx={styles.filler} />

          <div sx={styles.filterButtons}>
            <CategoriesFilterPanel position="vertical" productsCount={pagination.count} leftButtonText={t('resetFilters')} rightButtonText={t('ready')} handleLeftButtonClick={resetFilters} handleRightButtonClick={handleAsideClose} />
          </div>
        </div>
      </Aside>
    </ErrorBoundary>;
};
export default observer(CategoriesFilters);